import ApiClient from './ApiClient';

// Surfaces backend structured errors ({ error: "..." }) — mirrors TasksClient/ColoClient.
const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

// DTO payloads serialize PascalCase; wrapper keys are camelCase. Insights DTOs nest one level
// (health.collectors[], health.locations[]), so normalize recursively.
const toCamelKey = key => key.charAt(0).toLowerCase() + key.slice(1);

const toCamelCaseDeep = value => {
  if (Array.isArray(value)) {
    return value.map(toCamelCaseDeep);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, val]) => [
      toCamelKey(key),
      toCamelCaseDeep(val)
    ])
  );
};

class InsightsClient extends ApiClient {
  constructor(url) {
    super(url ? `${url}/insights` : '/insights');
  }

  async getIngestionHealth() {
    try {
      const data = await this.get('/ingestion-health');
      return toCamelCaseDeep(data?.health);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch ingestion health.')
      );
    }
  }

  async getUptimeReport(year, month) {
    try {
      const data = await this.get(`/uptime?year=${year}&month=${month}`);
      return toCamelCaseDeep(data?.devices ?? []);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch uptime report.')
      );
    }
  }

  async getAutoTopUpSummary() {
    try {
      const data = await this.get('/auto-topup');
      return {
        customers: toCamelCaseDeep(data?.customers ?? []),
        mollieConnected: !!data?.mollieConnected
      };
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch auto top-up summary.')
      );
    }
  }

  async getMarginReport() {
    try {
      const data = await this.get('/margin');
      return toCamelCaseDeep(data?.customers ?? []);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch margin report.')
      );
    }
  }
}

export default InsightsClient;
